import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnInit,
} from '@angular/core';
import { Store } from '@sama/app-state';
import { DirectiveSuperclass } from '@sama/ng-core';
import { Observable, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import { MathUtils } from 'three';
// eslint-disable-next-line import-whitelist -- Calibration options are loaded and updated from content
import { SettingsService } from '../../../core/h3ml/dsl/sensor-fusion/view-settings/settings.service';
import {
  CalibrationOptions,
  QuaternionFormatCorrection,
} from '../../../cross-team-shared/state/calibration-options-state';
import {
  Point2D,
  Point3D,
} from '../../../team-apis/otterdam/answers/shape-typings';
import { SingleFrameCalibrationData } from '../../../team-apis/otterdam/calibration-data';
import { CalibrationService } from '../../misc-services/calibration.service';
import { PointCloudContentStore } from '../../state/point-cloud-content-store';

interface PopoverInformation extends SingleFrameCalibrationData {
  fieldOfView: Point2D;
}

@Component({
  selector: 'h3-camera-calibration-popover',
  templateUrl: './camera-calibration-popover.component.html',
  styleUrls: ['./camera-calibration-popover.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CameraCalibrationPopoverComponent
  extends DirectiveSuperclass
  implements OnInit
{
  @Input({ required: true }) videoOutputName!: string;

  optionsStore$!: Observable<Store<CalibrationOptions>>;
  popoverInformation$!: Observable<PopoverInformation>;
  store = inject(PointCloudContentStore);

  private calibrationService = inject(CalibrationService);
  private settingsService = inject(SettingsService);

  ngOnInit() {
    this.popoverInformation$ = this.getInput$('videoOutputName').pipe(
      switchMap(() =>
        this.calibrationService.getCameraData$(this.videoOutputName).pipe(
          map((calibrationInformation) => ({
            ...calibrationInformation,
            fieldOfView: this.getFieldOfView(
              calibrationInformation.focalLength,
            ),
          })),
        ),
      ),
    );

    this.optionsStore$ = this.getInput$('videoOutputName').pipe(
      map(() =>
        this.store('render')('calibrationOptions')(this.videoOutputName),
      ),
    );
  }

  protected onQuaternionChange(
    quaternionFormatCorrections: QuaternionFormatCorrection,
  ) {
    this.settingsService.updateCalibrationOptions(this.videoOutputName, {
      quaternionFormatCorrections,
    });
  }

  protected onShouldInvertChange(shouldInvert: boolean) {
    this.settingsService.updateCalibrationOptions(this.videoOutputName, {
      shouldInvert,
    });
  }

  protected onShowAxesHelperChange(showAxesHelper: boolean) {
    this.settingsService.updateCalibrationOptions(this.videoOutputName, {
      showAxesHelper,
    });
  }

  protected onInitialRotationChange(value: number, index: number) {
    const { initialRotation } =
      this.store.state().render.calibrationOptions[this.videoOutputName];

    const newInitialRotation: Point3D = [...initialRotation];

    newInitialRotation[index] = value;

    this.settingsService.updateCalibrationOptions(this.videoOutputName, {
      initialRotation: newInitialRotation,
    });
  }

  // http://kmp.pentaxians.eu/technology/fov/#:~:text=The%20formula%20that%20it%20implements,smaller%20across%20the%20vertical%20dimension.
  private getFieldOfView(focalLength: Point2D) {
    const diagonal =
      Math.sqrt(focalLength[0] ** 2) + Math.sqrt(focalLength[1] ** 2);

    return [
      MathUtils.radToDeg(2 * Math.atan(diagonal / (2 * focalLength[0]))),
      MathUtils.radToDeg(2 * Math.atan(diagonal / (2 * focalLength[1]))),
    ] as Point2D;
  }
}
